// AssistantBookmarks — the AI Assistant's "Bookmarks" edge-tab list (Randy, Jun 2026).
// Jul 31, 2026 (Randy pt89c-xl) — pushToStaging fixed. Was using the wrong endpoint
// (`PATCH /workbench-state` which WIPES existing staging content on replace). Now
// uses the proper `POST /silos/{id}/send-to-staging` (used elsewhere for "send
// whole conversation to workbench") which APPENDS with a labeled + timestamped
// header and respects the trim cap. Also NAVIGATES to the silo's Workbench tab
// after the push so the ArrowSquareOut icon's "take me elsewhere" promise is kept.
// Lists this silo's saved conversations. Each row can: tap-to-LOAD the whole
// conversation back into the assistant, PLAY the audio that was saved with it
// (free, kept 7 days — greyed when there's none), SHARE it (same Export/Share
// dialog as the public silo), push it to this silo's Workbench STAGING, or TRASH it.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookmarkSimple, Play, Pause, SpeakerSlash, ShareNetwork, ArrowSquareOut, Trash, CaretRight, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ShareConversationSheet from "@/components/ShareConversationSheet";
import { useVoiceQA } from "@/lib/useVoiceQA";

const audioStillValid = (b) => {
  if (!b?.has_audio || !b?.audio_expires_at) return false;
  try { return new Date(b.audio_expires_at).getTime() > Date.now(); } catch { return false; }
};
// Jun 2026 (Randy) — the audio icon is GREEN (clickable to play the whole thing
// free) ONLY when the saved audio is COMPLETE + still alive. Partial/none → greyed.
const audioComplete = (b) => audioStillValid(b) && !!b?.audio_complete;
const audioPartial = (b) => audioStillValid(b) && !b?.audio_complete;

export default function AssistantBookmarks({ silo, refreshKey = 0, onLoad }) {
  const nav = useNavigate();
  // Jul 31, 2026 (Randy pt89c-xli) — two-state send button per-bookmark.
  // First tap sends to staging (button stays put, turns green, label swaps
  // to "Jump to workbench"). Second tap actually navigates. Matches the
  // AiBuddy per-answer + whole-conversation pattern so the platform's
  // "push to staging" affordance feels coherent everywhere.
  const [sentBookmarks, setSentBookmarks] = useState(() => new Set());
  const jumpToWorkbench = () => {
    if (!silo?.id) return;
    try { sessionStorage.setItem(`silo_tab:${silo.id}`, "workbench"); } catch { /* silent */ }
    nav(`/dashboard/silo/${silo.id}`);
  };
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [shareBm, setShareBm] = useState(null);
  // Jun 2026 (Randy) — play through the app's PERSISTENT two-voice engine (shared
  // singleton AudioContext) instead of a throwaway <audio>. This makes saved audio
  // (a) play in two voices in the order it was recorded, and (b) KEEP PLAYING when
  // you close this drawer or move elsewhere in the app — the engine's standalone
  // replay is designed to survive unmount. No teardown-on-close here.
  const voice = useVoiceQA({ slug: silo?.slug });

  const load = async () => {
    if (!silo?.slug) return;
    setLoading(true);
    try {
      const r = await api.get(`/bookmarks?slug=${encodeURIComponent(silo.slug)}`);
      setBookmarks(r.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [silo?.slug, refreshKey]);

  const stopAudio = () => { try { voice.stopSpeaking(); } catch { /* */ } setPlayingId(null); };

  const playAudio = async (b) => {
    if (playingId === b.id) { stopAudio(); return; }
    stopAudio();
    try {
      const r = await api.get(`/bookmarks/${b.id}/audio`);
      const chunks = (r.data?.chunks || []).map((c) => c.audio_base64).filter(Boolean);
      if (!chunks.length) { toast("That saved audio has expired — load the conversation to rebuild it.", { icon: "↪" }); return; }
      setPlayingId(b.id);
      voice.playPrefetched(chunks, { key: `bm-${b.id}` })
        .finally(() => setPlayingId((p) => (p === b.id ? null : p)));
    } catch { toast.error("Could not play the saved audio."); }
  };

  const onAudioTap = (b) => {
    if (audioComplete(b)) playAudio(b);
    else onLoad?.(b); // partial/none → load the convo (the assistant offers to complete it)
  };

  const removeBookmark = async (b) => {
    try {
      await api.delete(`/bookmarks/${b.id}`);
      setBookmarks((prev) => prev.filter((x) => x.id !== b.id));
      if (playingId === b.id) stopAudio();
      toast.success("Bookmark removed.");
    } catch { toast.error("Could not remove bookmark."); }
  };

  const pushToStaging = async (b) => {
    // Jul 31, 2026 (Randy pt89c-xli) — first tap sends only (marks the
    // bookmark as pushed → button flips to green "Jump to workbench").
    // Second tap navigates. No auto-nav so the user chooses when to jump.
    if (!silo?.id) {
      toast.error("Silo not ready — try again in a moment.");
      return;
    }
    const messages = (b.messages || [])
      .filter((m) => (m?.text || "").trim())
      .map((m) => ({ role: m.role === "user" ? "user" : "ai", text: m.text }));
    if (!messages.length) {
      toast.info("Nothing in this bookmark to push.");
      return;
    }
    try {
      await api.post(`/silos/${silo.id}/send-to-staging`, {
        label: b.file_title || "Saved conversation",
        messages,
      });
      setSentBookmarks((s) => { const n = new Set(s); n.add(b.id); return n; });
      toast.success("Sent to Workbench staging.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error("Only the silo owner can push to Workbench staging.");
      } else {
        toast.error(detail || "Could not push to staging. Please try again.");
      }
    }
  };

  if (loading && !bookmarks.length) {
    return <div className="h-full flex items-center justify-center text-sm text-[#9b9a96] p-8" data-testid="assistant-bookmarks-loading">Loading your saved conversations…</div>;
  }
  if (!bookmarks.length) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center text-sm text-[#9b9a96] p-8 gap-2" data-testid="assistant-bookmarks-empty">
        <BookmarkSimple size={26} weight="duotone" className="text-[#c9c8c2]" />
        <div>No saved conversations yet.</div>
        <div className="text-xs">Open the <span className="font-semibold">Tools</span> tab and tap “Bookmark this conversation”.</div>
      </div>
    );
  }

  return (
    <>
    <div className="p-3 space-y-2" data-testid="assistant-bookmarks-list">
      {bookmarks.map((b) => {
        const complete = audioComplete(b);
        const partial = audioPartial(b);
        const playing = playingId === b.id;
        return (
          <div key={b.id} className="rounded-lg border hairline bg-white p-3" data-testid={`assistant-bookmark-${b.id}`}>
            <button type="button" onClick={() => onLoad?.(b)} data-testid={`assistant-bookmark-open-${b.id}`}
              className="w-full text-left group">
              <div className="flex items-start gap-2">
                <CaretRight size={13} weight="bold" className="mt-1 text-[#9b9a96] group-hover:text-[#4A5D4E]" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#1C1B1A] truncate group-hover:text-[#4A5D4E]">{b.file_title || "Untitled"}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#9b9a96]">{b.section_name || "Saved"} · {(b.messages || []).length} msgs</div>
                </div>
              </div>
            </button>
            <div className="mt-2.5 flex items-center gap-1.5">
              <button type="button" onClick={() => onAudioTap(b)} data-testid={`assistant-bookmark-audio-${b.id}`}
                title={complete ? (playing ? "Stop" : "Play saved audio (free)") : partial ? "Partial audio — open to finish it" : "No saved audio — tap to load & rebuild"}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border hairline transition-colors ${complete ? "bg-[#27AE60] text-white border-[#27AE60] hover:opacity-90" : "bg-white text-[#b6b5b0]"}`}>
                {complete ? (playing ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />) : <SpeakerSlash size={12} weight="duotone" />}
                {complete ? (playing ? "Stop" : "Audio") : partial ? "Partial" : "No audio"}
              </button>
              <button type="button" onClick={() => setShareBm(b)} data-testid={`assistant-bookmark-share-${b.id}`}
                title="Share this conversation" className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE]">
                <ShareNetwork size={12} weight="duotone" /> Share
              </button>
              {/* Jul 31, 2026 (Randy pt89c-xli) — two-state button matches the AiBuddy
                  per-answer + whole-conversation Send flow. First tap sends (button
                  flips to green "Jump to workbench"). Second tap navigates. */}
              {sentBookmarks.has(b.id) ? (
                <button
                  type="button"
                  onClick={jumpToWorkbench}
                  data-testid={`assistant-bookmark-jump-${b.id}`}
                  title="Open your silo's Workbench tab to see the pushed transcript"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-[#27AE60] text-white hover:opacity-90"
                >
                  <ArrowSquareOut size={12} weight="duotone" /> Jump to workbench
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => pushToStaging(b)}
                  data-testid={`assistant-bookmark-staging-${b.id}`}
                  title="Push this saved conversation to your silo's Workbench staging"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border hairline bg-white text-[#1C1B1A] hover:bg-[#F0F1EE]"
                >
                  <ArrowSquareOut size={12} weight="duotone" /> Send to workbench
                </button>
              )}
              <button type="button" onClick={() => removeBookmark(b)} data-testid={`assistant-bookmark-trash-${b.id}`}
                title="Delete bookmark" className="inline-flex items-center justify-center w-7 h-7 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#C16E5A] ml-auto">
                <Trash size={13} weight="duotone" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
    {shareBm && (
      <ShareConversationSheet
        slug={silo.slug}
        siloName={silo.name}
        messages={shareBm.messages || []}
        bookmarkId={shareBm.id}
        answerVoice={silo.voice_id || "onyx"}
        onClose={() => { setShareBm(null); load(); }}
      />
    )}
    </>
  );
}
